import request from "./client";

export type OverviewTotals = {
  properties: number;
  rooms: number;
  tenants: number;
  tenancies: number;
  maintenanceIssues: number;
  rentPayments: number;
  guarantors: number;
  roomInspectionItems: number;
  roomInspections: number;
  roomInspectionResults: number;
  kitchenInspectionItems: number;
  kitchenInspections: number;
  kitchenInspectionResults: number;
  laundryInspectionItems: number;
  laundryInspections: number;
  laundryInspectionResults: number;
  activeTenancies: number;
  openMaintenanceIssues: number;
  occupiedRooms: number;
};

export type RentPaymentRecord = {
  id: number;
  tenancy_id: number;
  payment_date: string;
  payment_period?: string | null;
  amount: number;
  payment_source: "government" | "tenant" | "other";
  payment_method?: string | null;
  reference_no?: string | null;
  notes?: string | null;
};

export type BenefitTypeRecord = {
  id: number;
  benefit_name: string;
};

export type OverviewResponse = { totals: OverviewTotals };

export async function getOverview() {
  return request<OverviewResponse>("/api/overview");
}

export async function getRentPayments(tenancyId?: number) {
  const query = tenancyId ? `?tenancy_id=${tenancyId}` : "";
  const records = await request<RentPaymentRecord[]>(
    `/api/rent-payments${query}`,
  );
  return records.map((record) => ({
    ...record,
    id: Number(record.id),
    tenancy_id: Number(record.tenancy_id),
    amount: Number(record.amount),
  }));
}

export async function createRentPayment(payload: Record<string, unknown>) {
  const record = await request<RentPaymentRecord>("/api/rent-payments", {
    method: "POST",
    body: JSON.stringify(payload),
  });

  return {
    ...record,
    id: Number(record.id),
    tenancy_id: Number(record.tenancy_id),
    amount: Number(record.amount),
  };
}

export async function updateRentPayment(
  id: number,
  payload: Record<string, unknown>,
) {
  const record = await request<RentPaymentRecord>(`/api/rent-payments/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });

  return {
    ...record,
    id: Number(record.id),
    tenancy_id: Number(record.tenancy_id),
    amount: Number(record.amount),
  };
}

export async function deleteRentPayment(id: number) {
  return request<{ success: boolean }>(`/api/rent-payments/${id}`, {
    method: "DELETE",
  });
}

export async function getGuarantors() {
  return request<Array<Record<string, unknown>>>("/api/guarantors");
}

export async function getGasSafetyCertificates() {
  return request<Array<Record<string, unknown>>>(
    "/api/gas-safety-certificates",
  );
}

export async function getEicrCertificates() {
  return request<Array<Record<string, unknown>>>("/api/eicr-certificates");
}

export async function getGdprConsents() {
  return request<Array<Record<string, unknown>>>("/api/gdpr-consents");
}

export async function getAuditLogs() {
  return request<Array<Record<string, unknown>>>("/api/audit-logs");
}
